import React from "react";
import { ExternalLink } from "lucide-react";

import { PrimaryButton } from "@/shared/components/settings-atoms";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { clientAppLink } from "@/shared/lib/client-app";
import { InterfaceDialog } from "./InterfaceDialog";
import { LinkField } from "@/shared/components/LinkField";
import { LinkedAppsWarning } from "./LinkedAppsWarning";
import { Steps } from "./Steps";

import type { SetupProps } from "../types";

const HOW_PEOPLE_USE_IT = [
  <>They open the link and sign in with their Definable account. Who may come in is yours to choose in Settings › Policies: private, unlisted or public.</>,
  <>They chat with the agent, download what it makes for them, and connect their own accounts for the apps it uses theirs for.</>,
  <>On WhatsApp, Telegram or Slack, someone new is sent here first, and can link that chat to their account.</>,
];

/**
 * Every agent's client app: its page at the agent's own id, so there is nothing to turn on —
 * only its link to share. Who may come in is the agent's visibility, in Settings › Policies.
 */
export function ClientAppSetup({ agent, onClose }: SetupProps) {
  const meta = catalogFor("web")!;
  const url = clientAppLink(agent.id);

  return (
    <InterfaceDialog
      meta={meta}
      title="Share the client app"
      description={meta.desc}
      onClose={onClose}
      footer={
        <>
          <a href={url} target="_blank" rel="noreferrer"
            className="press inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12.5px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} /> Open
          </a>
          <PrimaryButton type="button" onClick={onClose}>Done</PrimaryButton>
        </>
      }
    >
      <LinkField url={url} />
      <Steps items={HOW_PEOPLE_USE_IT} />
      <LinkedAppsWarning agent={agent} />
    </InterfaceDialog>
  );
}
